<template>
  <div>
    <MissingProfileInformation
      v-model="missingProfileInformation"
      :has-valid-phone-number="hasValidPhoneNumber"
      :has-answered-security-question="hasAnsweredSecurityQuestion"
      :errors="form.errors"
      :security-questions="securityQuestions"
    />
    <ProfileName
      v-model="fullName"
      :errors="form.errors"
    />
    <ProfilePhoneNumberField
      v-if="hasValidPhoneNumber"
      v-model="phoneNumber"
      :errors="form.errors"
    />
    <UpdateSecurityQuestionField
      v-model="securityDetails"
      :errors="form.errors"
      :security-questions="securityQuestions"
      :has-answered-security-question="hasAnsweredSecurityQuestion"
    />
    <ProfileEmail
      v-model="form.email"
      :errors="form.errors"
    />
    <UpdatePasswordField
      v-model="updatePassword"
      :errors="form.errors"
    />
    <UpdateProfileDetailsButton
      :busy="form.busy"
      @updateProfile="updateProfile"
    />
    <ConfirmNumberModal
      :show="showConfirmNumberModal"
      :auth-code-id="authCodeId"
      message="Before we update your number please enter the 6 digit authorization code we just sent as a text message."
      @codeIsValid="handleValidCode"
      @cancel="handleCanceledCode"
    />
    <div
      v-if="showTestingHelpers"
      class="mb-4 mt-8 flex flex-row justify-center"
    >
      <button
        type="button"
        class="btn btn--danger btn--sm mx-auto"
        @click="resetMissingInformation"
        v-text="'TESTING: Reset missing information'"
      />
    </div>
  </div>
</template>

<script type="text/babel">
import MUMForm from 'Base/Resources/assets/js/MUMForm'
import ProfileName from './ProfileName'
import ProfileEmail from './ProfileEmail'
import ConfirmNumberModal from './ConfirmNumberModal'
import UpdatePasswordField from './UpdatePasswordField'
import ProfilePhoneNumberField from './ProfilePhoneNumberField'
import MissingProfileInformation from './MissingProfileInformation'
import UpdateProfileDetailsButton from './UpdateProfileDetailsButton'
import UpdateSecurityQuestionField from './UpdateSecurityQuestionField'

export default {
  name: 'ProfileDetails',
  components: {
    ProfileName,
    ProfileEmail,
    ConfirmNumberModal,
    UpdatePasswordField,
    ProfilePhoneNumberField,
    MissingProfileInformation,
    UpdateProfileDetailsButton,
    UpdateSecurityQuestionField,
  },
  props: {
    showTestingHelpers: {
      type: Boolean,
      required: false,
      default: false,
    },
    securityQuestions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      authCodeId: '',
      hasAnsweredSecurityQuestion: false,
      hasValidPhoneNumber: false,
      showConfirmNumberModal: false,
      form: new MUMForm({
        first_name: '',
        last_name: '',
        phone_number: '',
        phone_number_country: '',
        email: '',
        confirmation: null,
        update_security_question: false,
        security_question: this.securityQuestions[0].question,
        security_answer: '',
        password: '',
        password_confirmation: '',
      }),
    }
  },
  computed: {
    fullName: {
      get() {
        return {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
        }
      },
      set(value) {
        this.form.first_name = value.first_name
        this.form.last_name = value.last_name
      },
    },
    missingProfileInformation: {
      get() {
        return {
          phone_number: this.form.phone_number,
          phone_number_country: this.form.phone_number_country,
          security_question: this.form.security_question,
          security_answer: this.form.security_answer,
        }
      },
      set(value) {
        this.form.phone_number = value.phone_number
        this.form.phone_number_country = value.phone_number_country
        this.form.security_question = value.security_question
        this.form.security_answer = value.security_answer
      },
    },
    securityDetails: {
      get() {
        return {
          security_question: this.form.security_question,
          security_answer: this.form.security_answer,
          update_security_question: this.form.update_security_question,
        }
      },
      set(value) {
        this.form.security_question = value.security_question
        this.form.security_answer = value.security_answer
        this.form.update_security_question = value.update_security_question
      },
    },
    updatePassword: {
      get() {
        return {
          password: this.form.password,
          password_confirmation: this.form.password_confirmation,
        }
      },
      set(value) {
        this.form.password = value.password
        this.form.password_confirmation = value.password_confirmation
      },
    },
    phoneNumber: {
      get() {
        return {
          phone_number: this.form.phone_number,
          phone_number_country: this.form.phone_number_country,
        }
      },
      set(value) {
        this.form.phone_number = value.phone_number
        this.form.phone_number_country = value.phone_number_country
      },
    },
  },
  async mounted() {
    await this.$nextTick()

    this.$api.public()
      .get('/me')
      .then((response) => {
        this.form.first_name = response.data.attributes.first_name
        this.form.last_name = response.data.attributes.last_name
        this.form.phone_number = response.data.attributes.phone_number
        this.form.phone_number_country = response.data.attributes.phone_number_country
        this.form.email = response.data.attributes.email
        this.form.securityQuestion = response.data.attributes.security_question
        this.hasAnsweredSecurityQuestion = response.data.attributes.has_answered_security_question
        this.hasValidPhoneNumber = !!response.data.attributes.phone_number

        this.$emit('setInitialAvatar', response.data.attributes.avatar.small)
        this.$emit('showUserForm')
      })
  },
  methods: {
    handleValidCode(confirmation) {
      this.form.confirmation = confirmation
      this.showConfirmNumberModal = false

      this.updateProfile()
    },
    handleCanceledCode() {
      this.showConfirmNumberModal = false

      this.form.finishProcessing()

      this.$store.commit('baseAddNotification', {
        type: 'error',
        title: 'Error',
        text: 'Your profile was not updated.',
      })
    },
    updateProfile() {
      this.form.startProcessing()

      if(!this.form.password) {
        delete this.form.password
        delete this.form.password_confirmation
      }

      this.$api.public()
        .put('/me', this.form)
        .then(({ data }) => {
          if (data.status === 'sms_confirmation_required') {
            this.showConfirmNumberModal = true
            this.authCodeId = data.auth_code_id
          } else {
            this.form.security_answer = ''
            this.form.update_security_question = false
            this.hasValidPhoneNumber = true
            this.hasAnsweredSecurityQuestion = true

            this.form.finishProcessing()

            this.$store.commit('baseAddNotification', {
              type: 'success',
              title: 'Success!',
              text: 'Your NEP Connect Account details have been saved!',
            })
          }
        })
        .catch((errors) => {
          this.form.errors.set(errors.response.data)

          this.form.finishProcessing()

          if (!this.form.errors.any()) {
            this.$store.commit('baseAddNotification', {
              type: 'error',
              title: 'Error',
              text: 'There was a problem saving your profile information.',
            })
          }
        })
    },
    resetMissingInformation() {
      this.$api.public()
        .post('/me/reset-missing-information')
        .then(() => {
          if (confirm('Do you want to remove the phone number and security question?')) {
            location.reload()
          }
        })
    },
  },
}
</script>
